import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { toast } from '@/hooks/use-toast';

export interface UserPreferences {
  id: string;
  user_id: string;
  email_notifications: boolean;
  email_new_inquiries: boolean;
  email_new_messages: boolean;
  email_new_matches: boolean;
  email_listing_updates: boolean;
  email_marketing: boolean;
  profile_visibility: 'public' | 'private' | 'verified_only';
  show_email: boolean;
  show_phone: boolean;
  show_location: boolean;
  show_company: boolean;
  preferred_currency: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
  preferred_units: 'metric' | 'imperial';
  items_per_page: 10 | 20 | 50 | 100;
  theme_preference: 'light' | 'dark' | 'system';
  // Push notification preferences
  push_notifications: boolean;
  push_new_messages: boolean;
  push_new_matches: boolean;
  push_listing_updates: boolean;
  push_new_inquiries: boolean;
  created_at: string;
  updated_at: string;
}

const defaultPreferences: Partial<UserPreferences> = {
  email_notifications: true,
  email_new_inquiries: true,
  email_new_messages: true,
  email_new_matches: true,
  email_listing_updates: true,
  email_marketing: false,
  profile_visibility: 'public',
  show_email: false,
  show_phone: false,
  show_location: true,
  show_company: true,
  preferred_currency: 'USD',
  preferred_units: 'metric',
  items_per_page: 20,
  theme_preference: 'system',
  // Push defaults
  push_notifications: true,
  push_new_messages: true,
  push_new_matches: true,
  push_listing_updates: true,
  push_new_inquiries: true,
};

export const useUserPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (user) {
      fetchPreferences();
      subscribeToPreferences();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No preferences found, create default
          await createDefaultPreferences();
        } else {
          throw error;
        }
      } else {
        setPreferences(data as UserPreferences);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to load preferences',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createDefaultPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .insert({ user_id: user.id, ...defaultPreferences })
        .select()
        .single();

      if (error) throw error;
      setPreferences(data as UserPreferences);
    } catch (error) {
      console.error('Error creating default preferences:', error);
    }
  };

  const subscribeToPreferences = () => {
    if (!user) return;

    const channel = supabase
      .channel('user_preferences_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_preferences',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            setPreferences(payload.new as UserPreferences);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    if (!user || !preferences) return;

    setUpdating(true);
    
    // Optimistic update
    setPreferences({ ...preferences, ...updates });

    try {
      const { error } = await supabase
        .from('user_preferences')
        .update(updates)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: 'Settings saved',
        description: 'Your preferences have been updated',
      });
    } catch (error) {
      console.error('Error updating preferences:', error);
      // Revert optimistic update
      setPreferences(preferences);
      toast({
        title: 'Error',
        description: 'Failed to update preferences',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  return {
    preferences,
    loading,
    updating,
    updatePreferences,
    refetch: fetchPreferences,
  };
};
